import { useQuery, UseQueryResult } from "@tanstack/react-query";
import {
  auditLogService,
  AuditLog,
  AuditLogListResponse,
  GetAuditLogsParams,
  AuditSummaryResponse,
} from "../api/services/auditLogs/auditLogService";

interface UseAuditLogsOptions extends GetAuditLogsParams {
  enabled?: boolean;
}

export const useAuditLogs = (
  options: UseAuditLogsOptions = {},
): UseQueryResult<AuditLogListResponse> => {
  const { enabled = true, ...params } = options;

  return useQuery({
    queryKey: ["auditLogs", params],
    queryFn: () => auditLogService.getAuditLogs(params),
    enabled,
  });
};

export const useAuditLogSummary = (
  days: number = 7,
): UseQueryResult<AuditSummaryResponse> => {
  return useQuery({
    queryKey: ["auditLogSummary", days],
    queryFn: () => auditLogService.getAuditSummary(days),
  });
};

export const useUserActivity = (
  userId: string,
  startDate?: string,
  endDate?: string,
  enabled: boolean = true,
): UseQueryResult<AuditLog[]> => {
  return useQuery({
    queryKey: ["userActivity", userId, startDate, endDate],
    queryFn: () => auditLogService.getUserActivity(userId, startDate, endDate),
    enabled: enabled && !!userId,
  });
};

export const useRecordHistory = (
  tableName: string,
  recordId: string,
  enabled: boolean = true,
): UseQueryResult<AuditLog[]> => {
  return useQuery({
    queryKey: ["recordHistory", tableName, recordId],
    queryFn: () => auditLogService.getRecordHistory(tableName, recordId),
    enabled: enabled && !!tableName && !!recordId,
  });
};

export const useAuditLogSearch = (
  searchTerm: string,
  enabled: boolean = true,
): UseQueryResult<AuditLog[]> => {
  return useQuery({
    queryKey: ["auditLogSearch", searchTerm],
    queryFn: () => auditLogService.searchAuditLogs(searchTerm),
    enabled: enabled && searchTerm.length > 0,
  });
};
